import {
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
  getSpeakerNameFromLabel,
} from '@src/utility/speakerUtils';

import type { SpeakerWithColor } from '@src/services/SpeakerManagementService';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type {
  DatoSpeaker,
  SpeakerVoiceMapping,
} from '@src/utility/speakerUtils';

export interface SpeakerInfo {
  name: string;
  color: string;
  title?: string;
}

/**
 * Module-level cache for speaker data
 * Persists across component mounts/unmounts (e.g., view switches)
 */
export const speakerCache = new Map<string, Map<string, SpeakerInfo>>();

/**
 * Separate store for manually injected override labels.
 * These persist across cache clears and are merged into the speaker map on every load.
 */
export const injectedOverrides = new Map<string, Map<string, SpeakerInfo>>();

/**
 * Module-level cache for API response promises (speaker mappings + Dato speakers).
 * Keyed by storyId. The promise is stored so concurrent/subsequent callers
 * await the same in-flight request instead of issuing duplicates.
 * Cleared separately from speakerCache — only mutations that change mappings
 * or Dato speakers need to clear this (via clearSpeakerCache).
 */
const apiPromiseCache = new Map<
  string,
  Promise<[SpeakerVoiceMapping | null, DatoSpeaker[]]>
>();

/**
 * Fetches speaker mappings and Dato speakers, reusing an in-flight or resolved
 * promise for the same storyId. Call clearSpeakerCache() to force a refetch.
 */
export function fetchSpeakerApiData(
  storyId: string,
  datoClientStore: DatoClientStore,
): Promise<[SpeakerVoiceMapping | null, DatoSpeaker[]]> {
  const existing = apiPromiseCache.get(storyId);
  if (existing) return existing;

  const promise = Promise.all([
    fetchSpeakerVoiceMappings(storyId),
    fetchDatoSpeakers(storyId, datoClientStore),
  ]);
  apiPromiseCache.set(storyId, promise);

  // If the promise rejects, remove it so the next call retries
  promise.catch(() => {
    apiPromiseCache.delete(storyId);
  });

  return promise;
}

/**
 * Event name for speaker cache invalidation
 */
export const SPEAKER_CACHE_INVALIDATED_EVENT = 'speaker-cache-invalidated';
export const SPEAKER_ADDED_TO_STORY_EVENT = 'speaker-added-to-story';

/**
 * Dispatch when a speaker is added to a story so the panel can
 * do an optimistic UI update without waiting for DatoCMS CDN propagation.
 * Pass the full speaker: the panel uses it as its row, so a missing field
 * (e.g. gender) is blank in its Edit form and cleared on save.
 */
export function notifySpeakerAddedToStory(
  storyId: string,
  speaker: SpeakerWithColor,
  replacedSpeakerId?: string,
  mappedLabel?: string,
  mappedLabels?: string[],
) {
  window.dispatchEvent(
    new CustomEvent(SPEAKER_ADDED_TO_STORY_EVENT, {
      detail: {
        storyId,
        speaker,
        replacedSpeakerId,
        mappedLabel,
        mappedLabels,
      },
    }),
  );
}

/**
 * Injects a speaker label into the override store and notifies listeners.
 * Used for section overrides where the label doesn't exist in getFinalTranscriptionElements().
 * Overrides persist across cache clears and are merged into the speaker map on every load.
 */
export function injectSpeakerLabel(
  storyId: string,
  label: string,
  name: string,
  color: string,
) {
  let overrides = injectedOverrides.get(storyId);
  if (!overrides) {
    overrides = new Map();
    injectedOverrides.set(storyId, overrides);
  }
  overrides.set(label, { name, color });

  // Also put it in the main cache so it's available immediately
  let map = speakerCache.get(storyId);
  if (!map) {
    map = new Map();
    speakerCache.set(storyId, map);
  }
  map.set(label, { name, color });

  // Notify hooks to re-read from cache
  const event = new CustomEvent(SPEAKER_CACHE_INVALIDATED_EVENT, {
    detail: { storyId },
  });
  window.dispatchEvent(event);
}

/**
 * Write-through cache update for speaker assignments.
 * Updates the local speaker cache so labels resolve without a round-trip,
 * while the caller also persists to DatoCMS via addSpeakerReferenceToStory().
 *
 * Does NOT dispatch a notification event — call notifySpeakerCacheUpdated()
 * once after batching all writes to avoid N events for N labels.
 */
export function cacheAndWriteSpeakerAssignment(
  storyId: string,
  label: string,
  name: string,
  color: string,
) {
  let map = speakerCache.get(storyId);
  if (!map) {
    map = new Map();
    speakerCache.set(storyId, map);
  }
  map.set(label, { name, color });
}

/**
 * Notify hooks that the speaker cache has been updated.
 * Call once after batching cacheAndWriteSpeakerAssignment() writes.
 */
export function notifySpeakerCacheUpdated(storyId: string) {
  const event = new CustomEvent(SPEAKER_CACHE_INVALIDATED_EVENT, {
    detail: { storyId },
  });
  window.dispatchEvent(event);
}

/**
 * Updates a speaker's name directly in the cache without clearing it.
 * This avoids a DatoCMS CDN re-fetch that may return stale data after a recent update.
 *
 * When `datoSpeakerId` is provided, also updates the `override_<datoSpeakerId>` label
 * so that section-override labels stay in sync.
 *
 * @param storyId - The story ID
 * @param speakerLabel - The transcript speaker label (e.g., "spk_0")
 * @param newName - The updated speaker name
 * @param datoSpeakerId - Optional Dato speaker ID; updates `override_<id>` entry too
 */
export function updateSpeakerNameInCache(
  storyId: string,
  speakerLabel: string,
  newName: string,
  datoSpeakerId?: string,
) {
  updateSpeakerInCache(storyId, speakerLabel, { name: newName }, datoSpeakerId);
}

/**
 * Patches a speaker's `name` and/or `title` in the cache and notifies React.
 * Same write-through purpose as `updateSpeakerNameInCache`, but covers title
 * too (used by the clip-preview nametag editor's "Apply to all" path).
 */
export function updateSpeakerInCache(
  storyId: string,
  speakerLabel: string,
  updates: { name?: string; title?: string },
  datoSpeakerId?: string,
) {
  const map = speakerCache.get(storyId);
  if (!map) return;

  const newMap = new Map(map);

  const patch = (label: string) => {
    const existing = newMap.get(label);
    if (!existing) return;
    newMap.set(label, {
      ...existing,
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.title !== undefined ? { title: updates.title } : {}),
    });
  };

  patch(speakerLabel);
  if (datoSpeakerId) patch(`override_${datoSpeakerId}`);

  speakerCache.set(storyId, newMap);

  window.dispatchEvent(
    new CustomEvent(SPEAKER_CACHE_INVALIDATED_EVENT, {
      detail: { storyId },
    }),
  );
}

/**
 * Non-hook reader for the module-level speaker cache. Lets non-React code
 * (services, etc.) resolve a label to the same name the UI displays, falling
 * back to `Speaker N+1` for unmapped labels when the cache is cold.
 *
 * Mirrors the resolution `useSpeakerLabels().getSpeakerName` returns, minus
 * the React state plumbing.
 */
export function readSpeakerNameFromCache(
  storyId: string | undefined,
  speakerLabel: string | undefined,
): string {
  if (!speakerLabel) return 'Unknown';
  const cached = storyId
    ? speakerCache.get(storyId)?.get(speakerLabel)?.name
    : undefined;
  return cached || getSpeakerNameFromLabel(speakerLabel);
}

/**
 * Clears the speaker cache for a specific story and notifies listeners to refresh
 * @param storyId - Optional story ID to clear cache for. If not provided, clears all.
 */
export function clearSpeakerCache(storyId?: string) {
  if (storyId) {
    speakerCache.delete(storyId);
    injectedOverrides.delete(storyId);
    apiPromiseCache.delete(storyId);
  } else {
    speakerCache.clear();
    injectedOverrides.clear();
    apiPromiseCache.clear();
  }

  // Dispatch event to notify useSpeakerLabels hooks to refresh
  const event = new CustomEvent(SPEAKER_CACHE_INVALIDATED_EVENT, {
    detail: { storyId },
  });
  window.dispatchEvent(event);
}
